/**
 * Schema version 2 — the application data model. Phase 0 §5.3.
 *
 * Kept in its own file so that shipped versions read as a historical record
 * rather than a diff: v1 stays exactly as it shipped in `db.ts`, and this file
 * is only ever appended to by a future v3.
 *
 * **Only indexed properties are listed.** Dexie stores the whole object either
 * way; declaring an index costs write throughput, so each one below earns its
 * place by backing a query Phase 3–8 actually makes.
 *
 * `*field` declares a multi-entry index — one index entry per array element,
 * which is what lets "every exercise targeting `chest`" be an index lookup
 * rather than a full scan of 1,500 rows on a phone.
 */
export const V2_STORES: Record<string, string> = {
  // ── Carried forward from v1, unchanged ─────────────────────────────────
  // Restating them is required: Dexie treats each version's store map as the
  // complete schema, and omitting a store here would DELETE it.
  settings: 'key',
  meta: 'key',

  // ── Exercises ──────────────────────────────────────────────────────────
  // nameSearch backs search; source/isArchived/isFavourite back the filters;
  // the multi-entry indexes back taxonomy filtering.
  exercises:
    'id, nameSearch, source, isArchived, isFavourite, *bodyParts, *targetMuscles, *equipment, *tags',
  userMedia: 'id, exerciseId',

  // ── Plans ──────────────────────────────────────────────────────────────
  plans: 'id, isActive, isArchived',
  planDays: 'id, planId, [planId+orderIndex]',
  planDaySchedules: 'id, planDayId, weekday',
  planBlocks: 'id, planDayId, [planDayId+orderIndex]',
  planExercises: 'id, planBlockId, exerciseId',
  planSets: 'id, planExerciseId, [planExerciseId+setIndex+dropIndex]',
  progressionRules: 'id, planExerciseId',

  // ── Sessions ───────────────────────────────────────────────────────────
  sessions: 'id, startedAt, status, localDate, planId',
  sessionBlocks: 'id, sessionId',
  sessionExercises: 'id, sessionId, sessionBlockId, exerciseId',
  // The compound index is the uniqueness guard described in Phase 0 §5.3 —
  // it is what makes two identical primary sets detectable.
  sessionSets: 'id, sessionExerciseId, parentSetId, [sessionExerciseId+setIndex+dropIndex]',
  cardioSegments: 'id, sessionExerciseId',

  // ── Records, measurements, housekeeping ────────────────────────────────
  personalRecords: 'id, exerciseId, [exerciseId+kind+repCount]',
  measurementTypes: 'id, slug, orderIndex',
  bodyMeasurements: 'id, measurementTypeId, recordedAt, localDate',
  backupRecords: 'id, createdAt, kind',
  historyEditAudit: 'id, editedAt, entityId',
};

/**
 * Stores added by v2, i.e. every store above except the two v1 carried
 * forward. Used by the migration test to assert the upgrade actually created
 * them, rather than trusting that Dexie did.
 */
export const V2_NEW_STORES: readonly string[] = Object.keys(V2_STORES).filter(
  (name) => name !== 'settings' && name !== 'meta',
);
